<template>
  <div class="ps-panel">
    <div class="ps-panel-hd">
      <h2>Modo Offline</h2>
      <p>Permite usar o sistema sem internet e sincronizar ao reconectar.</p>
    </div>

    <!-- Instalar como app -->
    <div v-if="isInstallable && !isInstalled" class="install-app-card">
      <div class="install-app-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><path d="M12 8v8M8 12l4 4 4-4"/></svg>
      </div>
      <div class="install-app-text">
        <span class="install-app-title">Instalar como app no desktop</span>
        <span class="install-app-desc">Abre em janela própria, sem barra de endereço, como um app nativo.</span>
      </div>
      <button class="btn btn-primary btn-login" type="button" @click="promptInstall">Instalar</button>
    </div>
    <div v-else-if="isInstalled" class="install-app-card install-app-card--done">
      <div class="install-app-icon install-app-icon--done">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <div class="install-app-text">
        <span class="install-app-title">App instalado</span>
        <span class="install-app-desc">O ObraTycoon está instalado neste dispositivo como app.</span>
      </div>
    </div>

    <div class="offline-section">
      <div class="offline-row-header">
        <div class="offline-label">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M1 1l14 14M11.2 4.8A5 5 0 0 1 13 8M2.5 5.5A7 7 0 0 0 3.5 12M6 3.2A7 7 0 0 1 13 8m-6.5 1A2.5 2.5 0 0 1 10.5 8M5.5 12l.5.5a2.5 2.5 0 0 0 3.5 0L10 12" stroke-linecap="round"/></svg>
          Modo offline
        </div>
        <span class="offline-pill" :class="offlineEnabled ? 'offline-pill--on' : 'offline-pill--off'">
          {{ offlineEnabled ? 'Ativado' : 'Desativado' }}
        </span>
      </div>

      <div v-if="offlineEnabled" class="offline-stats-row">
        <div class="offline-stat-item">
          <span class="offline-stat-label">Última sincronização</span>
          <span class="offline-stat-val">{{ lastSyncLabel }}</span>
        </div>
        <div v-if="daysUntilBlock !== null" class="offline-stat-item">
          <span class="offline-stat-label">Dias restantes</span>
          <span class="offline-stat-val" :class="{ 'offline-stat-val--warn': daysUntilBlock <= 5 }">
            {{ daysUntilBlock === 0 ? 'Expirado' : `${daysUntilBlock}d` }}
          </span>
        </div>
        <div v-if="offlinePendingCount > 0" class="offline-stat-item">
          <span class="offline-stat-label">Pendentes</span>
          <span class="offline-stat-val offline-stat-val--warn">{{ offlinePendingCount }}</span>
        </div>
      </div>

      <div v-if="offlineDownloading" class="offline-progress">
        <div class="offline-progress-track">
          <div class="offline-progress-fill" :style="{ width: offlineDownloadPct + '%' }"></div>
        </div>
        <span class="offline-progress-label">Baixando… {{ offlineDownloadPct }}%</span>
      </div>

      <div class="offline-btns">
        <button v-if="!offlineEnabled" class="btn btn-primary btn-login" type="button" :disabled="offlineDownloading" @click="handleEnableOffline">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M6.5 1v8M3 6l3.5 3.5L10 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1 11h11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          Ativar modo offline
        </button>
        <template v-else>
          <button class="btn btn-primary btn-login" type="button" :disabled="offlineDownloading" @click="handleEnableOffline">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M11 6.5A4.5 4.5 0 112 6.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M11 3.5v3h-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Atualizar dados
          </button>
          <button class="btn btn-ghost btn-login" type="button" :disabled="offlineDownloading" @click="handleDisableOffline">Desativar</button>
        </template>
        <button class="btn btn-ghost btn-login storage-btn" type="button" @click="openStorageModal">
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><ellipse cx="7" cy="4" rx="5" ry="2" stroke="currentColor" stroke-width="1.3"/><path d="M2 4v2c0 1.1 2.24 2 5 2s5-.9 5-2V4" stroke="currentColor" stroke-width="1.3"/><path d="M2 6v2c0 1.1 2.24 2 5 2s5-.9 5-2V6" stroke="currentColor" stroke-width="1.3"/><path d="M2 8v2c0 1.1 2.24 2 5 2s5-.9 5-2V8" stroke="currentColor" stroke-width="1.3"/></svg>
          Armazenamento
        </button>
      </div>

      <button class="install-toggle" type="button" @click="showInstall = !showInstall">
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><rect x="2" y="1" width="10" height="12" rx="2" stroke="currentColor" stroke-width="1.3"/><path d="M5 4h4M5 7h4M5 10h2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
        Como adicionar no celular
        <svg class="install-chevron" :class="{ open: showInstall }" width="10" height="10" viewBox="0 0 11 11" fill="none"><path d="M2 4l3.5 3.5L9 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <div v-if="showInstall" class="install-instructions">
        <div class="install-platform">
          <p class="install-platform-name">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 1C4.134 1 1 4.134 1 8s3.134 7 7 7 7-3.134 7-7-3.134-7-7-7z" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 8l1.5 1.5L10.5 6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            iPhone / iPad (iOS)
          </p>
          <ol class="install-steps">
            <li>Abra o Safari e acesse este endereço</li>
            <li>Toque em compartilhar <strong>⎋</strong> na barra inferior</li>
            <li>Toque em <strong>"Adicionar à Tela de Início"</strong></li>
            <li>Confirme com <strong>"Adicionar"</strong></li>
          </ol>
        </div>
        <div class="install-platform">
          <p class="install-platform-name">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 3l4 4-4 4M9 11h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            Android (Chrome)
          </p>
          <ol class="install-steps">
            <li>Abra o Chrome e acesse este endereço</li>
            <li>Toque no menu <strong>⋮</strong> no canto superior direito</li>
            <li>Toque em <strong>"Adicionar à tela inicial"</strong></li>
            <li>Confirme com <strong>"Adicionar"</strong></li>
          </ol>
        </div>
      </div>
    </div>
  </div>

  <!-- STORAGE MODAL -->
  <div v-if="storageOpen" class="crop-overlay" @mousedown.self="storageOpen = false">
    <div class="storage-modal">
      <div class="storage-modal-header">
        <h3 class="storage-modal-title">
          <svg width="16" height="16" viewBox="0 0 14 14" fill="none"><ellipse cx="7" cy="4" rx="5" ry="2" stroke="currentColor" stroke-width="1.4"/><path d="M2 4v2c0 1.1 2.24 2 5 2s5-.9 5-2V4" stroke="currentColor" stroke-width="1.4"/><path d="M2 6v2c0 1.1 2.24 2 5 2s5-.9 5-2V6" stroke="currentColor" stroke-width="1.4"/><path d="M2 8v2c0 1.1 2.24 2 5 2s5-.9 5-2V8" stroke="currentColor" stroke-width="1.4"/></svg>
          Armazenamento
        </h3>
        <button class="storage-close" type="button" @click="storageOpen = false">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>
      </div>
      <div v-if="storageLoading" class="storage-loading">
        <div class="storage-spinner"></div>
        Calculando…
      </div>
      <template v-else>
        <div class="storage-total-block">
          <div class="storage-total-numbers">
            <span class="storage-used-label">{{ storageUsedFmt }}</span>
            <span class="storage-quota-label">de {{ storageQuotaFmt }}</span>
          </div>
          <div class="storage-bar-track">
            <div class="storage-bar-fill" :style="{ width: storageUsedPct + '%', background: storageBarColor }"></div>
          </div>
          <p class="storage-bar-caption">{{ storageUsedPct }}% do armazenamento do navegador utilizado</p>
        </div>
        <div class="storage-breakdown">
          <div class="storage-breakdown-title">Detalhamento</div>
          <div class="storage-breakdown-row" v-for="item in storageBreakdown" :key="item.label">
            <div class="storage-breakdown-dot" :style="{ background: item.color }"></div>
            <span class="storage-breakdown-label">{{ item.label }}</span>
            <span class="storage-breakdown-value">{{ item.fmt }}</span>
          </div>
        </div>
        <div class="storage-actions">
          <button class="btn btn-ghost btn-login storage-clear-btn" type="button" :disabled="storageClearing" @click="clearStorage">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 3.5h10M5.5 3.5V2.5h3v1M3 3.5l.8 8.2a1 1 0 0 0 1 .8h4.4a1 1 0 0 0 1-.8L11 3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            {{ storageClearing ? 'Limpando…' : 'Limpar dados offline' }}
          </button>
          <button class="btn btn-primary btn-login" type="button" @click="storageOpen = false">Fechar</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue'
import {
  offlineModeEnabled, offlineDownloading, offlineDownloadPct,
  offlinePendingCount, daysUntilBlock, offlineLastOnlineAt,
  enableOfflineMode, disableOfflineMode
} from '@/composables/useOfflineMode'
import { isInstallable, isInstalled, promptInstall } from '@/store/installPromptStore'

export default defineComponent({
  name: 'ProfileOfflineTab',
  setup() {
    const showInstall = ref(false)
    const offlineEnabled = offlineModeEnabled

    const lastSyncLabel = computed(() => {
      const last = offlineLastOnlineAt.value
      if (!last) return 'Nunca sincronizado'
      return new Date(last).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    })

    const handleEnableOffline = async () => { await enableOfflineMode() }
    const handleDisableOffline = async () => { await disableOfflineMode() }

    // Storage modal
    const storageOpen = ref(false)
    const storageLoading = ref(false)
    const storageClearing = ref(false)
    const storageUsedBytes = ref(0)
    const storageQuotaBytes = ref(0)
    const storageCacheBytes = ref(0)
    const storageIDBBytes = ref(0)

    const fmtBytes = (b: number) => {
      if (b >= 1024 * 1024 * 1024) return (b / (1024 ** 3)).toFixed(2) + ' GB'
      if (b >= 1024 * 1024) return (b / (1024 ** 2)).toFixed(1) + ' MB'
      if (b >= 1024) return (b / 1024).toFixed(0) + ' KB'
      return b + ' B'
    }

    const storageUsedFmt = computed(() => fmtBytes(storageUsedBytes.value))
    const storageQuotaFmt = computed(() => fmtBytes(storageQuotaBytes.value))
    const storageUsedPct = computed(() => {
      if (!storageQuotaBytes.value) return 0
      return Math.min(100, Math.round((storageUsedBytes.value / storageQuotaBytes.value) * 100))
    })
    const storageBarColor = computed(() => {
      const p = storageUsedPct.value
      if (p >= 80) return '#dc2626'
      if (p >= 50) return '#f59e0b'
      return 'var(--accent, #0f4c81)'
    })
    const storageBreakdown = computed(() => [
      { label: 'Cache (recursos do app)', fmt: fmtBytes(storageCacheBytes.value), color: 'var(--accent, #0f4c81)' },
      { label: 'Dados offline (IndexedDB)', fmt: fmtBytes(storageIDBBytes.value), color: '#10b981' },
      { label: 'Outros', fmt: fmtBytes(Math.max(0, storageUsedBytes.value - storageCacheBytes.value - storageIDBBytes.value)), color: '#9ca3af' }
    ])

    const measureStorage = async () => {
      storageLoading.value = true
      try {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const est = await navigator.storage.estimate()
          storageUsedBytes.value = est.usage || 0
          storageQuotaBytes.value = est.quota || 0
          const detail = (est as Record<string, unknown>).usageDetails as Record<string, number> | undefined
          if (detail) {
            storageCacheBytes.value = detail.caches || 0
            storageIDBBytes.value = detail.indexedDB || 0
          } else {
            storageCacheBytes.value = 0
            storageIDBBytes.value = 0
            if ('caches' in window) {
              const cacheNames = await caches.keys()
              let cacheTotal = 0
              for (const name of cacheNames) {
                const cache = await caches.open(name)
                const keys = await cache.keys()
                for (const req of keys) {
                  const res = await cache.match(req)
                  if (res) { const buf = await res.clone().arrayBuffer(); cacheTotal += buf.byteLength }
                }
              }
              storageCacheBytes.value = cacheTotal
              storageIDBBytes.value = Math.max(0, (est.usage || 0) - cacheTotal)
            }
          }
        }
      } catch { /* unsupported */ } finally {
        storageLoading.value = false
      }
    }

    const openStorageModal = async () => {
      storageOpen.value = true
      await measureStorage()
    }

    const clearStorage = async () => {
      storageClearing.value = true
      try {
        const { clearAllOfflineData } = await import('@/utils/offlineDB')
        await clearAllOfflineData()
        if ('caches' in window) {
          const names = await caches.keys()
          await Promise.all(names.map(n => caches.delete(n)))
        }
        await measureStorage()
      } catch { /* ignore */ } finally {
        storageClearing.value = false
      }
    }

    return {
      showInstall, offlineEnabled, lastSyncLabel,
      offlineDownloading, offlineDownloadPct, offlinePendingCount, daysUntilBlock,
      handleEnableOffline, handleDisableOffline,
      storageOpen, storageLoading, storageClearing,
      storageUsedFmt, storageQuotaFmt, storageUsedPct, storageBarColor,
      storageBreakdown, openStorageModal, clearStorage,
      isInstallable, isInstalled, promptInstall,
    }
  }
})
</script>

<style scoped lang="scss">
.ps-panel-hd { margin-bottom: 24px; h2 { font-size: 18px; font-weight: 700; color: var(--ink); margin: 0 0 4px; } p { font-size: 13px; color: var(--ink-muted); margin: 0; } }

.install-app-card {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 18px; border-radius: 12px; border: 1px solid var(--border);
  background: var(--surface); margin-bottom: 16px;
  &--done { background: #f0fdf4; border-color: #86efac; }
}
.install-app-icon {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  background: rgba(15,76,129,.1); color: var(--system-navbar, #0f4c81);
  display: flex; align-items: center; justify-content: center;
  &--done { background: #dcfce7; color: #166534; }
}
.install-app-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.install-app-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.install-app-desc { font-size: 12px; color: var(--ink-muted); }

.offline-section {
  display: flex; flex-direction: column; gap: 16px;
  background: var(--card, #fff); border: 1px solid var(--border, rgba(31,30,26,.1));
  border-radius: 12px; padding: 20px;
}

.offline-row-header { display: flex; align-items: center; justify-content: space-between; }

.offline-label {
  display: flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 600; color: var(--ink);
  svg { opacity: .5; }
}

.offline-pill {
  font-size: 11px; font-weight: 700; border-radius: 20px; padding: 3px 10px; text-transform: uppercase; letter-spacing: .04em;
  &--on  { background: #e8f5ee; color: #1f8a45; }
  &--off { background: rgba(31,30,26,.07); color: var(--ink-muted); }
}

.offline-stats-row { display: flex; gap: 20px; flex-wrap: wrap; }
.offline-stat-item { display: flex; flex-direction: column; gap: 2px; }
.offline-stat-label { font-size: 11px; color: var(--ink-muted); font-weight: 500; text-transform: uppercase; letter-spacing: .04em; }
.offline-stat-val { font-size: 14px; font-weight: 700; color: var(--ink); &--warn { color: #d97706; } }

.offline-progress { display: flex; flex-direction: column; gap: 6px; }
.offline-progress-track { height: 5px; background: rgba(31,30,26,.1); border-radius: 99px; overflow: hidden; }
.offline-progress-fill { height: 100%; background: var(--accent, #0f4c81); border-radius: 99px; transition: width .3s; }
.offline-progress-label { font-size: 12px; color: var(--ink-muted); }

.offline-btns { display: flex; gap: 8px; flex-wrap: wrap; }

.storage-btn { display: inline-flex; align-items: center; gap: 5px; }

.install-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer; font-size: 13px;
  color: var(--ink-muted); padding: 0; text-align: left;
  &:hover { color: var(--ink); }
}
.install-chevron { transition: transform .2s; flex-shrink: 0; &.open { transform: rotate(180deg); } }
.install-instructions {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  border: 1px solid var(--border, rgba(31,30,26,.12)); border-radius: 10px; padding: 14px; background: rgba(31,30,26,.02);
  @media (max-width: 600px) { grid-template-columns: 1fr; }
}
.install-platform-name { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: var(--ink); margin: 0 0 8px; }
.install-steps { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; li { font-size: 13px; color: var(--ink-muted); line-height: 1.5; } }

.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid transparent; border-radius: var(--radius, 6px); padding: 6px 16px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background .12s; }
.btn-primary { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); &:hover { filter: brightness(1.1); } &:disabled { opacity: .5; cursor: not-allowed; } }
.btn-ghost { background: transparent; color: var(--ink); border-color: var(--border-strong); &:hover { background: var(--surface-3); } &:disabled { opacity: .5; cursor: not-allowed; } }
.btn-login { padding: 10px 22px; border-radius: 8px; }

// Storage modal
.crop-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.72); z-index: 9999; display: flex; align-items: center; justify-content: center; }
.storage-modal { background: var(--surface, #fff); border-radius: 16px; padding: 24px; width: min(440px, 94vw); display: flex; flex-direction: column; gap: 20px; box-shadow: 0 24px 64px rgba(0,0,0,.35); }
.storage-modal-header { display: flex; align-items: center; justify-content: space-between; }
.storage-modal-title { margin: 0; font-size: 16px; font-weight: 600; color: var(--ink); display: flex; align-items: center; gap: 8px; }
.storage-close { background: none; border: none; cursor: pointer; color: var(--ink-muted); padding: 4px; border-radius: 6px; display: flex; &:hover { background: rgba(31,30,26,.07); } }
.storage-loading { display: flex; align-items: center; gap: 10px; font-size: 14px; color: var(--ink-muted); padding: 16px 0; }
.storage-spinner { width: 18px; height: 18px; border: 2px solid rgba(31,30,26,.15); border-top-color: var(--accent, #0f4c81); border-radius: 50%; animation: spin .7s linear infinite; flex-shrink: 0; }
@keyframes spin { to { transform: rotate(360deg); } }
.storage-total-block { display: flex; flex-direction: column; gap: 8px; }
.storage-total-numbers { display: flex; align-items: baseline; gap: 6px; }
.storage-used-label { font-size: 28px; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
.storage-quota-label { font-size: 14px; color: var(--ink-muted); }
.storage-bar-track { height: 8px; background: rgba(31,30,26,.1); border-radius: 99px; overflow: hidden; }
.storage-bar-fill { height: 100%; border-radius: 99px; transition: width .4s ease, background .3s; }
.storage-bar-caption { margin: 0; font-size: 12px; color: var(--ink-muted); }
.storage-breakdown { display: flex; flex-direction: column; gap: 10px; padding: 16px; border: 1px solid var(--border, rgba(31,30,26,.12)); border-radius: 10px; background: rgba(31,30,26,.02); }
.storage-breakdown-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-muted); margin-bottom: 4px; }
.storage-breakdown-row { display: flex; align-items: center; gap: 8px; }
.storage-breakdown-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.storage-breakdown-label { flex: 1; font-size: 13px; color: var(--ink-muted); }
.storage-breakdown-value { font-size: 13px; font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
.storage-actions { display: flex; gap: 8px; justify-content: flex-end; }
.storage-clear-btn { display: inline-flex; align-items: center; gap: 5px; }
</style>
